import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import '../styles/FTSOSubmissions.css'; // Use FTSO Submissions styles
import {
  getHeaderStatus,
  getHeaderClass,
  getHeaderIcon,
  getPercentageClass,
  getCountClass,
  getMetricClass,
  calculateSubmissionsAverage
} from '../utils/statusHelpers';
import { StatusEvaluator } from '../context/StatusEvaluator';
import { LastGoodStatusStore } from '../utils/LastGoodStatusStore';

function FDCDataSummary({ 
  data: summaryDataAll,
  fdcDataAll,
  onMissingRoundsClick
}) {
  const [animatingSections, setAnimatingSections] = useState({});
  const [sectionsState, setSectionsState] = useState({
    fdc: true,
  });
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, transform: 'translateX(-50%) translateY(-100%)' });
  const [tooltipData, setTooltipData] = useState({ entity: '', network: '', timeframe: '', timeframeData: null, invalidAttestations: [] });

  const summaryConfigs = [
    { entity: 'ENV', network: 'flare', title: 'Envision Flare' },
    { entity: 'ENV', network: 'songbird', title: 'Envision Songbird' },
    { entity: 'UYS', network: 'flare', title: 'UYS Flare' },
    { entity: 'UYS', network: 'songbird', title: 'UYS Songbird' },
  ];

  const toggleSection = (section) => {
    if (sectionsState[section]) {
      // Closing - start exit animation
      setAnimatingSections(prev => ({
        ...prev,
        [section]: 'closing'
      }));
      
      // Remove after animation completes
      setTimeout(() => {
        setSectionsState(prev => ({
          ...prev,
          [section]: false
        }));
        setAnimatingSections(prev => ({
          ...prev,
          [section]: null
        }));
      }, 500); // Match animation duration
    } else {
      // Opening
      setSectionsState(prev => ({
        ...prev,
        [section]: true
      }));
      setAnimatingSections(prev => ({
        ...prev,
        [section]: 'opening'
      }));
    }
  };

  const getFDCHeaderStatus = (entityData) => {
    if (!entityData) return 'red';
  
    // Use attestation percentage for header status from actual data structure
    const sixHourPercentage = entityData?.['6hr']?.percentage || 0;
    
    if (sixHourPercentage >= 95) {
      return 'green';
    } else if (sixHourPercentage >= 85) {
      return 'amber';
    } else {
      return 'red';
    }
  };

  const calculateFDCAverage = (entityData) => {
    if (!entityData) return 0;
    
    // Get 6hr attestation data and calculate valid/total percentage (for display only)
    const sixHourData = entityData?.['6hr'];
    if (!sixHourData) return 0;

    const valid = sixHourData?.attestations?.valid || sixHourData?.valid_attestations || 0;
    const total = sixHourData?.attestations?.total || sixHourData?.total_attestations || 0;

    if (total === 0) return 0;

    const percentage = (valid / total) * 100;
    
    // Show decimal places if not a round number
    const rounded = Math.round(percentage);
    if (Math.abs(percentage - rounded) < 0.01) {
      // Very close to a round number, show as integer
      return rounded;
    } else {
      // Not a round number, show one decimal place
      return Math.round(percentage * 10) / 10;
    }
  };

  // Function to get default data structure when no data exists
  const getDefaultFDCData = () => ({
    '15min': {
      attestations: { percentage: 0 },
      signatures: { percentage: 0 },
      reveal_offences: { count: 0 },
      missing_rounds: []
    },
    '2hr': {
      attestations: { percentage: 0 },
      signatures: { percentage: 0 },
      reveal_offences: { count: 0 },
      missing_rounds: []
    },
    '6hr': {
      attestations: { percentage: 0 },
      signatures: { percentage: 0 },
      reveal_offences: { count: 0 },
      missing_rounds: []
    }
  });

  // Get invalid attestations for a specific entity/network/timeframe
  const getInvalidAttestations = (entity, network, timeframe) => {
    if (!fdcDataAll || !fdcDataAll[entity] || !fdcDataAll[entity][network]) {
      console.log('No fdcDataAll found for', entity, network);
      return [];
    }

    const entityNetworkData = fdcDataAll[entity][network];
    
    // Data structure: object keyed by voting round numbers
    
    let attestationArray = [];
    if (Array.isArray(entityNetworkData)) {
      attestationArray = entityNetworkData;
    } else if (typeof entityNetworkData === 'object') {
      // Get all voting rounds and flatten their attestation arrays
      const votingRoundKeys = Object.keys(entityNetworkData);
      
      attestationArray = [];
      votingRoundKeys.forEach(vrKey => {
        const vrData = entityNetworkData[vrKey];
        if (Array.isArray(vrData)) {
          // Add voting round ID to each attestation
          const attestationsWithVR = vrData.map(att => ({...att, voting_round_id: vrKey}));
          attestationArray = attestationArray.concat(attestationsWithVR);
        } else if (vrData && typeof vrData === 'object') {
          // If each voting round contains an object with attestations array
          if (vrData.attestations && Array.isArray(vrData.attestations)) {
            // Add voting round ID to each attestation
            const attestationsWithVR = vrData.attestations.map(att => ({...att, voting_round_id: vrKey}));
            attestationArray = attestationArray.concat(attestationsWithVR);
          } else {
            // If the voting round data itself is an attestation record
            attestationArray.push({...vrData, voting_round_id: vrKey});
          }
        }
      });
    }
    
    // Optional debug logging
    // console.log('Attestation array length:', attestationArray.length);
    
    const votingRounds = {
      '15min': 10,
      '2hr': 80, 
      '6hr': 240
    };
    
    // Get the latest voting round from the summary data instead
    const latestVotingRound = summaryDataAll?.[entity]?.[network]?.latest_voting_round || 0;
    const roundsInTimeframe = votingRounds[timeframe] || 10;
    const minRound = latestVotingRound - roundsInTimeframe;
    
    // Just return ALL invalid attestations - the backend already calculated the correct timeframe totals
    const invalidAttestations = attestationArray.filter(attestation => 
      attestation.bitvote_result !== true
    );
    

    
    return invalidAttestations;
  };

  // Hover handlers for tooltip
  const handleMouseEnter = (event, entity, network, timeframe, timeframeData) => {
    const valid = timeframeData?.attestations?.valid || timeframeData?.valid_attestations || 0;
    const total = timeframeData?.attestations?.total || timeframeData?.total_attestations || 0;
    
    // Only show if there are invalid attestations
    if (valid < total) {
      const rect = event.currentTarget.getBoundingClientRect();
      const tooltipWidth = 320; // Max width estimate
      const tooltipHeight = 200; // Estimate
      const gap = 10;
      
      // Center horizontally on the element
      let x = rect.left + rect.width / 2;
      let y = rect.top - gap;
      let transform = 'translateX(-50%) translateY(-100%)';
      
      // Check horizontal boundaries
      if (x - tooltipWidth / 2 < 0) {
        // Too close to left edge
        x = rect.left;
        transform = 'translateY(-100%)';
      } else if (x + tooltipWidth / 2 > window.innerWidth) {
        // Too close to right edge
        x = rect.right;
        transform = 'translateX(-100%) translateY(-100%)';
      }
      
      // Check vertical boundaries
      if (rect.top - tooltipHeight < 0) {
        y = rect.bottom + gap;
        if (transform.includes('translateX(-50%)')) {
          transform = 'translateX(-50%)';
        } else if (transform.includes('translateX(-100%)')) {
          transform = 'translateX(-100%)';
        } else {
          transform = '';
        }
      }
      
      // Get the specific invalid attestations
      const invalidAttestations = getInvalidAttestations(entity, network, timeframe);
      
      // Set position and data first
      setTooltipPosition({ x, y, transform });
      setTooltipData({ 
        entity, 
        network, 
        timeframe, 
        timeframeData, 
        invalidAttestations 
      });
      
      // Show tooltip in next frame to ensure position is applied first
      requestAnimationFrame(() => {
        setShowTooltip(true);
      });
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Function to get conditional formatting for total attestations
  const getTotalAttestationsClass = (total, timeframe) => {
    const votingRounds = {
      '15min': 10,
      '2hr': 80, 
      '6hr': 240
    };
    
    const expectedRounds = votingRounds[timeframe] || 1;
    const minExpected = expectedRounds * 1; 
    
    if (total === 0) {
      return 'color-red';
    } else if (total < minExpected) {
      return 'color-amber';
    } else {
      return 'color-green';
    }
  };

  // Function to get conditional formatting for valid attestations
  // Function to get color class for FDC attestation percentage display
  const getFDCAttestationPercentageClass = (percentage) => {
    if (percentage === 100) {
      return 'color-green';
    } else if (percentage >= 99) {
      return 'color-amber';
    } else {
      return 'color-red';
    }
  };

  const getValidAttestationsClass = (valid, total) => {
    if (total === 0) {
      return 'color-red'; // No attestations at all
    } else if (valid === total) {
      return 'color-green'; // All valid
    } else if (valid === total - 1) {
      return 'color-amber'; // One invalid
    } else {
      return 'color-red'; // More than one invalid
    }
  };
  
  return (
    <>
      <div className="data-summary">
        <div className="summary-boxes">
                  {summaryConfigs.map(({ entity, network, title }) => {
          const entitySubmissionSummary = summaryDataAll?.[entity]?.[network] || getDefaultFDCData();
          const latestRoundForDisplay = summaryDataAll?.[entity]?.[network]?.latest_voting_round || 'N/A';

          let headerStatus = StatusEvaluator.evaluateFdcSummaryBoxStatus(entitySubmissionSummary);
          if (!headerStatus) {
            const lg = LastGoodStatusStore.get('fdc-submissions', `${entity}|${network}`);
            if (lg) headerStatus = lg;
          } else {
            LastGoodStatusStore.set('fdc-submissions', `${entity}|${network}`, headerStatus);
          }
          const headerClass = getHeaderClass(headerStatus || 'red');
          const headerIconSrc = getHeaderIcon(headerStatus);

            return (
              <div key={`${entity}-${network}`} className="summary-box">
                <h2 className={headerClass}>
                  <div className="summary-box-title-group">
                    <span>{title}</span>
                    <span className="data-summary-latest-round">
                      ({latestRoundForDisplay})
                    </span>
                  </div>
                  <img
                    src={headerIconSrc}
                    alt="Status Icon"
                    className="header-icon"
                  />
                </h2>

                <div className="summary-content">
                  <div className="summary-section">
                    {/* Flare Data Connector Section */}
                    <div
                      className="section-header"
                      onClick={() => toggleSection('fdc')}
                    >
                      <span className="section-title">ATTESTATIONS</span>
                      <span
                        className={`section-value ${getFDCAttestationPercentageClass(
                          calculateFDCAverage(entitySubmissionSummary)
                        )}`}
                      >
                        {calculateFDCAverage(entitySubmissionSummary)}%
                      </span>
                      <img
                        src="https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671a2db89cdd4d25e9d6855a_dropdown.svg"
                        alt="Toggle"
                        className={`dropdown-icon ${
                          sectionsState.fdc ? 'open' : ''
                        }`}
                      />
                    </div>

                    {sectionsState.fdc && (
                      <div className={`summary-grid ${animatingSections.fdc || ''}`}>
                        <span className="grid-header"></span>
                        <span className="grid-header">15min</span>
                        <span className="grid-header">2hr</span>
                        <span className="grid-header">6hr</span>

                        {/* Total Attestations row */}
                        <span className="method-label" style={{"--row-index": 0}}>total:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => {
                          const timeframeData = entitySubmissionSummary?.[timeframe];
                          const total = timeframeData?.attestations?.total || timeframeData?.total_attestations || 0;
                          
                          return (
                            <span
                              key={`total-${timeframe}`}
                              className={`percentage ${getTotalAttestationsClass(total, timeframe)}`}
                              style={{"--row-index": index + 1}}
                            >
                              {total}
                            </span>
                          );
                        })}

                        {/* Valid Attestations row */}
                        <span className="method-label" style={{"--row-index": 4}}>valid:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => {
                          const timeframeData = entitySubmissionSummary?.[timeframe];
                          const valid = timeframeData?.attestations?.valid || timeframeData?.valid_attestations || 0;
                          const total = timeframeData?.attestations?.total || timeframeData?.total_attestations || 0;
                          const percentage = total > 0 ? Math.round((valid / total) * 100) : 0;
                          const showHover = valid < total; // Only show when there are invalid attestations
                          
                          // Hover functionality works when valid < total
                          
                          return (
                            <span
                              key={`valid-${timeframe}`}
                              className={`percentage ${getValidAttestationsClass(valid, total)}`}
                              style={{"--row-index": index + 5, cursor: showHover ? 'pointer' : 'default'}}
                              onMouseEnter={showHover ? (e) => handleMouseEnter(e, entity, network, timeframe, timeframeData) : undefined}
                              onMouseLeave={showHover ? handleMouseLeave : undefined}
                            >
                              {valid}
                            </span>
                          );
                        })}

                        {/* Reveal Offences row */}
                        <span className="method-label" style={{"--row-index": 8}}>offences:</span>
                        {['15min', '2hr', '6hr'].map((timeframe, index) => {
                          const timeframeData = entitySubmissionSummary?.[timeframe];
                          const count = timeframeData?.reveal_offences?.count || 0;
                          
                          return (
                            <span
                              key={`off-${timeframe}`}
                              className={`percentage ${getCountClass(count)}`}
                              style={{"--row-index": index + 9}}
                            >
                              {count}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Tooltip Modal */}
      {showTooltip && createPortal(
        <div 
          className="fdc-attestation-modal"
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: tooltipPosition.transform,
            zIndex: 9999,
            minWidth: '220px',
            maxWidth: '320px',
            padding: '16px',
            pointerEvents: 'none'
          }}
        >
                    <div className="fdc-attestation-modal-header">
            Invalid Attestations ({tooltipData.invalidAttestations?.length || 0}):
          </div>
          
          {(() => {
            const data = tooltipData.timeframeData;
            const total = data?.attestations?.total || data?.total_attestations || 0;
            const valid = data?.attestations?.valid || data?.valid_attestations || 0;
            const invalid = total - valid;
            const missingRounds = data?.missing_rounds || [];
            const offences = data?.reveal_offences?.count || 0;
            
            return (
              <>

                  
                {tooltipData.invalidAttestations && tooltipData.invalidAttestations.length > 0 ? (
                  <>
                    {tooltipData.invalidAttestations.map((attestation, index) => (
                      <div key={index} className="fdc-attestation-modal-item">
                        <div
                          className="fdc-status-circle invalid"
                          style={{ marginRight: '12px', minWidth: '12px', flexShrink: 0 }}
                        />
                        <div className="fdc-attestation-modal-text">
                          <span className="fdc-attestation-modal-type">
                            VR {attestation.voting_round_id || 'N/A'}: 
                          </span>
                          <span className="fdc-attestation-modal-source">
                            {attestation.attestation_type} ({attestation.source_id})
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="fdc-attestation-modal-item">
                    <div
                      className="fdc-status-circle invalid"
                      style={{ marginRight: '12px', minWidth: '12px', flexShrink: 0 }}
                    />
                    <div className="fdc-attestation-modal-text">
                      <span className="fdc-attestation-modal-type">
                        No detailed data available
                      </span>
                      <span className="fdc-attestation-modal-source">
                        {invalid} invalid out of {total} total
                      </span>
                    </div>
                  </div>
                )}
                

                
                {offences > 0 && (
                  <div className="fdc-attestation-modal-item">
                    <div
                      className="fdc-status-circle invalid"
                      style={{ marginRight: '12px', minWidth: '12px', flexShrink: 0 }}
                    />
                    <div className="fdc-attestation-modal-text">
                      <span className="fdc-attestation-modal-type">
                        Reveal Offences: 
                      </span>
                      <span className="fdc-attestation-modal-source">
                        {offences}
                      </span>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>,
        document.body
      )}
    </>
  );
}

export default FDCDataSummary; 